import ServiceCollection from 'src/services/ServiceCollection';
import React, { useContext, useEffect, useMemo } from 'react';
import { Button } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import SegmentFeatureShell from 'src/components/segment-cdt/SegmentFeatureShell';
import SegmentPageHeader from 'src/components/segment-cdt/SegmentPageHeader';
import { ACTIONS } from 'src/services/segment-cdt/SegmentTablesReducer';
import SegmentTablesGrid from 'src/components/segment-cdt/list/SegmentTablesGrid';
import SegmentCreateWizard from 'src/components/segment-cdt/create/SegmentCreateWizard';
import { toSegmentTableListRow } from 'src/models/segment-cdt/SegmentTableListRow';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';

/**
 * List body of the Segment Data Tables tab. Reads `{ state, dispatch, services }` from the shell's
 * context, fetches the list (re-fetching on `refreshToken` bumps after a create), and renders the
 * header (count + Create) + grid + create wizard. Lives inside `SegmentFeatureShell`.
 */
function SegmentTablesListContent() {
    const { state, dispatch, services } = useContext(SegmentTablesContext);
    const { canAddNew } = getPermissions(AppModules.CUSTOM_DATA_TABLES);

    // Stable request; a new identity (on refreshToken bump) re-runs the list fetch. A create success
    // dispatches ACTIONS.REFRESH to reload the list without knowing what changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const listRequest = useMemo(() => ({}), [state.refreshToken]);
    const [listResponse, loading, error] = services.segmentDataTablesService.listSegmentDataTables(listRequest);

    const rows = useMemo(() => (listResponse?.tables ?? []).map(toSegmentTableListRow), [listResponse]);

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(error)) {
            services.messageService.showErrorBanner(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    return (
        <>
            <SegmentPageHeader
                title={
                    <>
                        <span>Segment Data Tables </span>
                        <span className="customTablesRecordCounter">({rows.length})</span>
                    </>
                }
                actions={
                    canAddNew && (
                        <Button variant="primary" onClick={() => dispatch(ACTIONS.OPEN_CREATE_WIZARD)}>Create Segment Table</Button>
                    )
                }
            />
            <div className="tableContainer">
                <SegmentTablesGrid
                    loading={loading}
                    tables={rows}
                    tableDetailUrl={CONSTANTS.PAGE_NAV.SEGMENT_DATA_TABLE_DETAILS.URL}
                />
            </div>
            <SegmentCreateWizard />
        </>
    );
}

/**
 * The Segment Data Tables tab: a read-only list of segment CDTs plus the Create entry point, hosted in
 * the shared `SegmentFeatureShell` (which provides `SegmentTablesContext`, the breadcrumb, and the page
 * container). Backed by ListSegmentDataTables (no data until that server op ships).
 */
export default function SegmentTablesView(props: { services: ServiceCollection }) {
    const { CUSTOM_TABLES } = CONSTANTS.PAGE_NAV;
    return (
        <SegmentFeatureShell
            services={props.services}
            breadcrumb={{ label: CUSTOM_TABLES.TITLE, title: CUSTOM_TABLES.TITLE, description: CUSTOM_TABLES.DESCRIPTION }}
        >
            <SegmentTablesListContent />
        </SegmentFeatureShell>
    );
}
